/*
Copyright (C) 2026  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React from "react";
import styled from "styled-components";

import { Download, Trash2, Upload } from "lucide-react";

import { T9 } from "@src/components/modules/WizardModule/WizardKit";
import { ActionButton } from "@src/components/ui/DetailsKit/DetailsKit";
import InfoIcon from "@src/components/ui/InfoIcon";
import DomUtils from "@src/utils/DomUtils";
import FileUtils from "@src/utils/FileUtils";

import type { InstanceScript } from "@src/@types/Instance";
import {
  USER_SCRIPT_PHASE_DESCRIPTIONS,
  USER_SCRIPT_PHASE_OPTIONS,
  USER_SCRIPT_PHASES,
  UserScriptPhase,
} from "@src/@types/Instance";

export type PhaseScript = { content: string | null; fileName: string | null };
export type ScriptsByPhase = Partial<Record<UserScriptPhase, PhaseScript>>;

const Wrapper = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
`;
const Phases = styled.div`
  border: 1px solid ${T9.slate200};
  border-radius: 8px;
`;
const PhaseRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 34px;
  border-top: 1px solid ${T9.slate200};
  padding: 12px 16px;
  &:first-child {
    border-top: none;
  }
`;
const PhaseLabel = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: ${T9.slate900};
`;
const InfoIconStyled = styled(InfoIcon)`
  margin-left: 8px;
`;
const PhaseControl = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  margin-left: 16px;
`;
const FileName = styled.div`
  max-width: 160px;
  font-size: 13px;
  color: ${T9.slate600};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;
const ActionLink = styled.button<{ red?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  color: ${props => (props.red ? "var(--t9-danger-700)" : T9.brand600)};
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;
const FakeFileInput = styled.input`
  position: absolute;
  opacity: 0;
  top: -99999px;
`;
const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
`;

type Props = {
  title: string;
  global: "windows" | "linux" | null;
  instanceId: string | null;
  scriptsByPhase: ScriptsByPhase;
  onRequestClose: () => void;
  onSave: (scripts: InstanceScript[]) => void;
};
type State = {
  scriptsByPhase: ScriptsByPhase;
};

class UserScriptsModal extends React.Component<Props, State> {
  fileInputRefs: { [phase: string]: HTMLInputElement | null } = {};

  constructor(props: Props) {
    super(props);
    this.state = { scriptsByPhase: { ...props.scriptsByPhase } };
  }

  getPhaseLabel(phase: UserScriptPhase): string {
    return (
      USER_SCRIPT_PHASE_OPTIONS.find(o => o.value === phase)?.label || phase
    );
  }

  setPhaseScript(phase: UserScriptPhase, script: PhaseScript) {
    this.setState(prevState => ({
      scriptsByPhase: { ...prevState.scriptsByPhase, [phase]: script },
    }));
  }

  async handleFileUpload(phase: UserScriptPhase, files: FileList | null) {
    if (!files || !files.length) {
      return;
    }
    const fileName = files[0].name;
    const content = await FileUtils.readTextFromFirstFile(files);
    this.setPhaseScript(phase, { content: content ?? null, fileName });
  }

  handleRemove(phase: UserScriptPhase) {
    this.setPhaseScript(phase, { content: null, fileName: null });
    const ref = this.fileInputRefs[phase];
    if (ref) {
      ref.value = "";
    }
  }

  handleDownload(phase: UserScriptPhase) {
    const entry = this.state.scriptsByPhase[phase];
    if (!entry?.content) {
      return;
    }
    const baseName = this.props.global || this.props.instanceId || "script";
    DomUtils.download(entry.content, entry.fileName || `${baseName}_${phase}`);
  }

  handleSave() {
    const scripts: InstanceScript[] = USER_SCRIPT_PHASES.reduce<
      InstanceScript[]
    >((acc, phase) => {
      const entry = this.state.scriptsByPhase[phase];
      if (entry && entry.content && entry.content.trim() !== "") {
        acc.push({
          global: this.props.global,
          instanceId: this.props.instanceId,
          phase,
          scriptContent: entry.content,
          fileName: entry.fileName ?? null,
        });
      }
      return acc;
    }, []);
    this.props.onSave(scripts);
    this.props.onRequestClose();
  }

  render() {
    return (
      <Wrapper>
        <Phases>
        {USER_SCRIPT_PHASES.map(phase => {
          const entry = this.state.scriptsByPhase[phase];
          const hasContent = Boolean(entry?.content);
          return (
            <PhaseRow key={phase}>
              <PhaseLabel>
                {this.getPhaseLabel(phase)}
                <InfoIconStyled text={USER_SCRIPT_PHASE_DESCRIPTIONS[phase]} />
              </PhaseLabel>
              <PhaseControl>
                {hasContent ? (
                  <>
                    <FileName title={entry?.fileName || "Script selected"}>
                      {entry?.fileName || "Script selected"}
                    </FileName>
                    <ActionLink
                      type="button"
                      onClick={() => this.handleDownload(phase)}
                    >
                      <Download size={14} />
                      Download
                    </ActionLink>
                    <ActionLink
                      type="button"
                      red
                      onClick={() => this.handleRemove(phase)}
                    >
                      <Trash2 size={14} />
                      Remove
                    </ActionLink>
                  </>
                ) : (
                  <ActionLink
                    type="button"
                    onClick={() => {
                      this.fileInputRefs[phase]?.click();
                    }}
                  >
                    <Upload size={14} />
                    Choose file…
                  </ActionLink>
                )}
                <FakeFileInput
                  type="file"
                  ref={(r: HTMLInputElement) => {
                    this.fileInputRefs[phase] = r;
                  }}
                  onChange={e => {
                    this.handleFileUpload(phase, e.target.files);
                  }}
                />
              </PhaseControl>
            </PhaseRow>
          );
        })}
        </Phases>
        <Buttons>
          <ActionButton
            type="button"
            variant="secondary"
            onClick={() => this.props.onRequestClose()}
          >
            Cancel
          </ActionButton>
          <ActionButton type="button" onClick={() => this.handleSave()}>
            Save
          </ActionButton>
        </Buttons>
      </Wrapper>
    );
  }
}

export default UserScriptsModal;
